/**
 * What the scorebook screen knows that its notices must say, as ONE record and
 * a pure reducer (delta review of 8060fa5).
 *
 * Earlier rounds kept the game's status in several holders (a list of facts
 * per notice, a slot, the unsure notice's own copy) that were updated by
 * different paths and so disagreed: a notice could tell the coach to finish a
 * game they had since reopened, or to re-record an entry into a game that was
 * final. Here every Finish and Reopen outcome updates one record of the
 * server's status, and every sentence about it is DERIVED from that record:
 * the takeover and wipe notices' status sentence, the persistent unsure
 * notice, and whether the game may be final for the entry sentences.
 *
 * No I/O and no React: `ScorebookScreen.tsx` dispatches events and renders
 * what these functions return, and `bookFacts.test.ts` drives every sequence
 * of outcomes through them.
 */

import {
  FINISH_CONSEQUENCE,
  REOPEN_CONSEQUENCE,
  TAKEOVER_ENTRY_SHORT,
  TAKEOVER_SHORT,
  TAKEOVER_UNCERTAIN_SHORT,
  noticeSentences,
  type SupersededOutcome,
} from './supersededCopy.js';
import { LOST_PLAY_SHORT, LOST_SHORT, type LostFact, lostBody } from './unavailable.js';

/**
 * The latest thing known about the game's status on the server, from this
 * page's own Finish and Reopen calls, or null when this page has made none
 * that could have changed it.
 */
export type ServerStatus = 'final' | 'live' | 'mayBeFinal' | 'mayBeLive';

export interface BookFacts {
  readonly server: ServerStatus | null;
  /**
   * Whether this phone's cached status may disagree with `server`: the
   * local half of a Finish or Reopen was not confirmed, or the server's
   * answer did not confirm the call. False once a call succeeds outright.
   */
  readonly behind: boolean;
  /** An entry made on this page may not have been recorded. */
  readonly entry: boolean;
  /**
   * The book on screen may not match disk, because a re-read after a write of
   * unknown outcome failed; the diagnostic of that write. Cleared by a later
   * re-read that works.
   */
  readonly stale: { readonly diagnostic: string } | null;
}

export const initialBookFacts: BookFacts = {
  server: null,
  behind: false,
  entry: false,
  stale: null,
};

/**
 * One outcome, in the screen's terms.
 *
 * `status`: a Finish or Reopen whose server half did or may have committed.
 * `plain` succeeded outright; `notKept` reached the server but its local half
 * was not confirmed (`StatusNotKept`, an uncertain write, or a stop met by
 * it); `mayHave` got an answer that does not confirm it (`mayHaveCommitted`), or
 * none at all after the request was sent (an `OfflineError`: the connection
 * can drop after the commit). A 4xx, `ROLE_NOT_IMPLEMENTED`, and an offline
 * failure raised before the request was sent (`neverSent`, from the platform
 * network adapter) committed nothing and are not dispatched at all.
 */
export type BookEvent =
  | {
      readonly type: 'status';
      readonly act: 'finish' | 'reopen';
      readonly result: 'plain' | 'notKept' | 'mayHave';
    }
  | { readonly type: 'entry' }
  | { readonly type: 'reread'; readonly ok: true }
  | { readonly type: 'reread'; readonly ok: false; readonly diagnostic: string };

export function reduceBookFacts(state: BookFacts, event: BookEvent): BookFacts {
  switch (event.type) {
    case 'entry':
      return state.entry ? state : { ...state, entry: true };
    case 'reread':
      return event.ok
        ? { ...state, stale: null }
        : { ...state, stale: { diagnostic: event.diagnostic } };
    case 'status': {
      const known: ServerStatus = event.act === 'finish' ? 'final' : 'live';
      if (event.result === 'plain') return { ...state, server: known, behind: false };
      if (event.result === 'notKept') return { ...state, server: known, behind: true };
      // An answer that confirms nothing leaves a status already known to be
      // the one asked for as it is: finishing a final game cannot reopen it.
      if (state.server === known) return state;
      return {
        ...state,
        server: event.act === 'finish' ? 'mayBeFinal' : 'mayBeLive',
        behind: true,
      };
    }
  }
}

/** A status sentence, in both notice builders' terms. */
export type StatusOutcome = 'finished' | 'reopened' | 'mayHaveFinished' | 'mayHaveReopened';

/** The status sentence's outcome, when this phone may disagree with the server. */
export function statusOutcomeOf(state: BookFacts): StatusOutcome | null {
  if (!state.behind || state.server === null) return null;
  switch (state.server) {
    case 'final':
      return 'finished';
    case 'live':
      return 'reopened';
    case 'mayBeFinal':
      return 'mayHaveFinished';
    case 'mayBeLive':
      return 'mayHaveReopened';
  }
}

/**
 * Whether the game is or may be final on the server, which is what decides
 * whether an entry sentence may invite entering it again. True after a plain
 * Finish too, when no status sentence is shown at all.
 */
export function mayBeFinalOf(state: BookFacts): boolean {
  return state.server === 'final' || state.server === 'mayBeFinal' || state.server === 'mayBeLive';
}

/** The facts both stop notices say: an entry that may be missing, and the status sentence. */
function factsOf(state: BookFacts): readonly ('refused' | StatusOutcome)[] {
  const status = statusOutcomeOf(state);
  return [...(state.entry ? (['refused'] as const) : []), ...(status === null ? [] : [status])];
}

/** The takeover notice's facts. */
export function noticeOutcomesOf(state: BookFacts): readonly SupersededOutcome[] {
  return factsOf(state);
}

/** The wipe notice's facts. */
export function noticeFactsOf(state: BookFacts): readonly LostFact[] {
  return factsOf(state);
}

/** The body a stop's notice shows over the book, without a reference. */
export function stopNotice(
  state: BookFacts,
  stop: 'takeover' | 'wipe',
  storeMayBeGone: boolean,
): string {
  return stop === 'takeover'
    ? noticeSentences(noticeOutcomesOf(state), storeMayBeGone, true, mayBeFinalOf(state))
    : lostBody(noticeFactsOf(state), true, mayBeFinalOf(state));
}

export interface Copy {
  readonly title: string;
  readonly body: string;
}

/**
 * The step before repeating a Finish or a Reopen whose outcome this phone
 * cannot learn by itself: nothing is written locally for it, and the phone has
 * no way to ask the server. The repeat is not harmless. Another device may have
 * reopened the game since, and finishing a game that is open makes the score
 * entered here its final score; or another device may have finished it, and a
 * Reopen opens it again over that Finish. Each caution names its consequence.
 */
export const FINISH_CAUTION = `If the book still shows the game live, make sure nobody has reopened it on another device before you finish it again. ${FINISH_CONSEQUENCE}`;
export const REOPEN_CAUTION = `If the book still shows the game finished, make sure nobody has finished it on another device since, before you reopen it again. ${REOPEN_CONSEQUENCE}`;

/** What a Finish or Reopen with no confirmed outcome leaves this phone unable to do. */
const CANNOT_FIND_OUT = 'This phone cannot find out which by itself.';

/**
 * A Finish or Reopen the server took, or may have taken, that this phone may
 * not show. `stale` is whether the book on screen could not be read again.
 * "The server did not confirm it" is true both for an answer that confirmed
 * nothing (a 5xx, an unreadable 200) and for no answer at all. The repeat is
 * offered with its consequence (`FINISH_CAUTION`, `REOPEN_CAUTION`), never as
 * safe: the game may have been changed on another device since.
 */
export function statusCopy(outcome: StatusOutcome, stale: boolean): Copy {
  const where = stale
    ? 'The book on screen may not match what this phone holds. Reload this page.'
    : 'The book now shows what this phone holds.';
  switch (outcome) {
    case 'finished':
      return {
        title: 'The game was finished on the server.',
        body: `This phone may not have kept that. ${where} ${FINISH_CAUTION}`,
      };
    case 'reopened':
      return {
        title: 'The game was reopened on the server.',
        body: `This phone may not have kept that. ${where} ${REOPEN_CAUTION}`,
      };
    case 'mayHaveFinished':
      return {
        title: 'The server may have finished this game.',
        body: `The server did not confirm it. ${CANNOT_FIND_OUT} ${where} ${FINISH_CAUTION}`,
      };
    case 'mayHaveReopened':
      return {
        title: 'The server may have reopened this game.',
        body: `The server did not confirm it. ${CANNOT_FIND_OUT} ${where} ${REOPEN_CAUTION}`,
      };
  }
}

/** A Finish or Reopen whose answer confirmed nothing (a 5xx, an unreadable 200). */
export function mayHaveCopy(reopening: boolean): Copy {
  return {
    title: reopening
      ? 'The server may have reopened this game.'
      : 'The server may have finished this game.',
    body: `Its answer did not confirm it either way. ${CANNOT_FIND_OUT} ${
      reopening ? REOPEN_CAUTION : FINISH_CAUTION
    }`,
  };
}

/**
 * A Finish or Reopen that was sent and got no answer: the connection may have
 * dropped after the server took it. Never "did not go through".
 */
export function lostAnswerCopy(reopening: boolean): Copy {
  return {
    title: 'That may not have gone through.',
    body: `The connection dropped before the server answered, so the game may or may not have been ${
      reopening ? 'reopened' : 'finished'
    }. ${CANNOT_FIND_OUT} ${reopening ? REOPEN_CAUTION : FINISH_CAUTION}`,
  };
}

/**
 * The persistent notice when a re-read after a write of unknown outcome
 * failed, or null when the book on screen is current. Derived, so a later
 * Finish or Reopen changes what it says as it changes the record.
 */
export function unsureNotice(state: BookFacts): Copy | null {
  if (state.stale === null) return null;
  const status = statusOutcomeOf(state);
  if (status !== null) return statusCopy(status, true);
  // No entry was made on this page that may be missing: the stale book came
  // from a Finish or Reopen whose status is now settled, so there is nothing
  // to enter again, only a book to bring up to date.
  if (!state.entry) {
    return {
      title: 'The book on screen may be out of date.',
      body: "The book on screen may not match what this phone holds. Reload this page, then check the game's status and the score.",
    };
  }
  return {
    title: 'That may or may not have been saved.',
    body: `The book on screen may not match what this phone holds. Reload this page, then check the timeline and the score${
      mayBeFinalOf(state) ? '.' : ' before you enter it again.'
    }`,
  };
}

/**
 * The one-line form of a stop notice, without its reference, for a screen with no
 * room for the body (ADR-0083, ADR-0086). Which sentence it is follows
 * the stop and the facts that change what the coach must do: a wipe says to write
 * the plays down; a takeover that may also have wiped the store says the same;
 * over either, a refused play is named ahead of the write-down step, since it
 * is not on the screen to copy; a certain takeover says what
 * happened, and a play that may be lost with the step to reload and check, or a
 * Finish or Reopen the server may have taken, because a takeover refuses a tap
 * without a dialog and this row may be all the coach is told. No short form
 * offers to repeat or re-enter anything.
 */
export function stopShort(
  stop: 'takeover' | 'wipe',
  storeMayBeGone: boolean,
  state: BookFacts = initialBookFacts,
): string {
  // Over a store that may be lost the write-down step leads; a refused play is
  // named too, because it is not on the screen the coach is told to copy.
  if (stop === 'wipe' || storeMayBeGone) {
    if (state.entry) return LOST_PLAY_SHORT;
    return stop === 'wipe' ? LOST_SHORT : TAKEOVER_UNCERTAIN_SHORT;
  }
  if (state.entry) return TAKEOVER_ENTRY_SHORT;
  const status = statusOutcomeOf(state);
  if (status !== null) return STATUS_SHORT[status];
  return TAKEOVER_SHORT;
}

const STATUS_SHORT: Readonly<Record<StatusOutcome, string>> = {
  finished: 'Finished on server. Reload.',
  reopened: 'Reopened on server. Reload.',
  mayHaveFinished: 'May be finished on server.',
  mayHaveReopened: 'May be reopened on server.',
};

/**
 * The one-line form of the unsure notice, without its reference, or null when
 * `unsureNotice` is null. Derived from the same record, so it can never say
 * what the full notice does not.
 */
export function unsureShort(state: BookFacts): string | null {
  if (state.stale === null) return null;
  const status = statusOutcomeOf(state);
  if (status !== null) return STATUS_SHORT[status];
  if (!state.entry) return 'Book may be out of date. Reload.';
  return 'Entry may be unsaved. Reload.';
}

/** Every combination of the facts a stop's short form can depend on. */
export function everyBookFacts(): readonly BookFacts[] {
  const servers: readonly ServerStatus[] = ['final', 'live', 'mayBeFinal', 'mayBeLive'];
  return [null, ...servers].flatMap((server) =>
    [false, true].flatMap((behind) =>
      [false, true].map((entry) => ({ server, behind, entry, stale: null })),
    ),
  );
}

/**
 * Every one-line form the book can show, named by the facts that select it:
 * both stops, certain and uncertain, over every combination of facts, and the
 * unsure notice over every combination with a stale book. The tests iterate
 * this instead of listing forms by hand, so a form added to a builder is
 * checked without anyone remembering to add it.
 */
export function everyShortForm(): readonly { readonly name: string; readonly short: string }[] {
  const describe = (state: BookFacts) =>
    `server=${String(state.server)} behind=${String(state.behind)} entry=${String(state.entry)}`;
  return everyBookFacts().flatMap((state) => {
    const stale = { ...state, stale: { diagnostic: 'SB-TST' } };
    const unsure = unsureShort(stale);
    return [
      { name: `wipe ${describe(state)}`, short: stopShort('wipe', true, state) },
      { name: `uncertain takeover ${describe(state)}`, short: stopShort('takeover', true, state) },
      { name: `certain takeover ${describe(state)}`, short: stopShort('takeover', false, state) },
      ...(unsure === null ? [] : [{ name: `unsure ${describe(state)}`, short: unsure }]),
    ];
  });
}
